import React from 'react';
import { 
  FileText,
  Calendar,
  Clock,
  MapPin,
  User,
  DollarSign,
  CheckCircle2,
  Briefcase,
  Users,
  Plane,
  Hotel,
  Car,
  Mail,
  Phone
} from 'lucide-react';

const ExecutiveBriefTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(`2000-01-01T${timeString}`);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch {
      return timeString;
    }
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.lastName}, ${quote.client.firstName}`;
    }
    return 'Client';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Minimal, Document Style */}
      <div className="border-b-2 border-black">
        <div className="max-w-6xl mx-auto px-8 py-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Travel Brief</div>
              <h1 className="text-2xl font-bold text-black">
                {quote.quoteTitle || 'Executive Travel Itinerary'}
              </h1>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Reference</div>
              <div className="font-mono text-sm font-bold text-black">{quote.quoteNumber}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Executive Summary Table */}
      <div className="max-w-6xl mx-auto px-8 py-8">
        <div className="border border-gray-300">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-gray-300">
                <td className="px-4 py-3 bg-gray-50 font-semibold text-gray-700 w-48">Traveler</td>
                <td className="px-4 py-3 font-mono">{getClientName()}</td>
              </tr>
              {quote.travelDates?.start && (
                <tr className="border-b border-gray-300">
                  <td className="px-4 py-3 bg-gray-50 font-semibold text-gray-700">Travel Dates</td>
                  <td className="px-4 py-3 font-mono">
                    {formatDate(quote.travelDates.start)} - {formatDate(quote.travelDates.end)}
                  </td>
                </tr>
              )}
              {(quote.travelers?.adults || quote.travelers?.children?.length) && (
                <tr className="border-b border-gray-300">
                  <td className="px-4 py-3 bg-gray-50 font-semibold text-gray-700">Party Size</td>
                  <td className="px-4 py-3 font-mono">
                    {quote.travelers.adults || 0} Adult(s)
                    {quote.travelers.children?.length > 0 && `, ${quote.travelers.children.length} Child(ren)`}
                  </td>
                </tr>
              )}
              <tr>
                <td className="px-4 py-3 bg-gray-50 font-semibold text-gray-700">Total Investment</td>
                <td className="px-4 py-3 font-mono font-bold text-black">{formatCurrency(quote.totalPrice)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Itinerary Components - Spreadsheet Style */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-black mb-4 uppercase tracking-wide">Detailed Itinerary</h2>
          
          {quote.components && quote.components.length > 0 ? (
            <div className="border border-gray-300">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-black text-white">
                    <th className="px-4 py-3 text-left font-semibold">#</th>
                    <th className="px-4 py-3 text-left font-semibold">Type</th>
                    <th className="px-4 py-3 text-left font-semibold">Description</th>
                    <th className="px-4 py-3 text-left font-semibold">Details</th>
                    <th className="px-4 py-3 text-right font-semibold">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.components.map((component, index) => (
                    <React.Fragment key={component.id || index}>
                      <tr className="border-b border-gray-300 hover:bg-gray-50">
                        <td className="px-4 py-4 font-mono text-gray-600">{index + 1}</td>
                        <td className="px-4 py-4">
                          <span className="inline-block px-2 py-1 bg-gray-100 text-xs font-semibold uppercase tracking-wide">
                            {component.type || 'Item'}
                          </span>
                        </td>
                        <td className="px-4 py-4 font-semibold">{component.title || `Component ${index + 1}`}</td>
                        <td className="px-4 py-4">
                          {component.data && Object.keys(component.data).length > 0 && (
                            <div className="space-y-1 text-xs">
                              {Object.entries(component.data).map(([key, value]) => {
                                if (!value || key === 'isOption' || key === 'optionDescription') return null;
                                
                                // Check if this is a price field
                                const isPriceField = key.toLowerCase().includes('price') || 
                                                     key.toLowerCase().includes('cost') || 
                                                     key.toLowerCase().includes('fee') ||
                                                     key.toLowerCase().includes('total') ||
                                                     key.toLowerCase().includes('deposit');
                                const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                                const isTime = key.toLowerCase().includes('time');
                                const isConfirmation = key.toLowerCase().includes('confirmation') || 
                                                       key.toLowerCase().includes('reference') ||
                                                       key.toLowerCase().includes('booking');
                                
                                let displayValue = String(value);
                                if (isPriceField && isNumeric) {
                                  displayValue = formatCurrency(parseFloat(value));
                                }
                                
                                return (
                                  <div key={key} className="flex gap-2">
                                    <span className="text-gray-500 uppercase tracking-wide min-w-32">
                                      {key.replace(/_/g, ' ')}:
                                    </span>
                                    <span className={`${isTime || isConfirmation ? 'font-mono font-semibold' : ''} text-gray-900`}>
                                      {displayValue}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right font-mono font-bold">{formatCurrency(component.price)}</td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <td colSpan="5" className="px-4 py-4">
                          {/* Component Photos */}
                          {component.photos && component.photos.length > 0 && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                              <h4 className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
                                <span>📸</span> Attachments
                              </h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {component.photos.map((photo, photoIndex) => (
                                  <div 
                                    key={photoIndex} 
                                    className="relative group cursor-pointer rounded overflow-hidden shadow hover:shadow-md transition-shadow border border-gray-300 bg-white"
                                    onClick={() => window.open(photo.url, '_blank')}
                                  >
                                    <img 
                                      src={photo.url} 
                                      alt={photo.name || 'Component photo'} 
                                      className="w-full h-auto object-contain"
                                      style={{ maxHeight: '300px' }}
                                    />
                                    <div className="absolute inset-0 bg-gray-900/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <span className="text-white text-xs font-semibold">{photo.name || 'View'}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Component Documents */}
                          {component.documents && component.documents.length > 0 && (
                            <div className="mt-4">
                              <h4 className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider flex items-center gap-2">
                                <span>📋</span> Documents
                              </h4>
                              <div className="space-y-2">
                                {component.documents.map((doc, docIndex) => (
                                  <a 
                                    key={docIndex}
                                    href={doc.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded border border-gray-300 text-sm font-mono"
                                  >
                                    <span>📄</span>
                                    <span className="text-gray-900">{doc.name}</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                  
                  {/* Total Row */}
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="4" className="px-4 py-4 text-right uppercase tracking-wide">Total</td>
                    <td className="px-4 py-4 text-right font-mono text-lg">{formatCurrency(quote.totalPrice)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border border-gray-300 p-8 text-center text-gray-500">
              No itinerary items
            </div>
          )}
        </div>

        {/* Payment Summary */}
        {quote.depositAmount > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-bold text-black mb-4 uppercase tracking-wide">Payment Schedule</h2>
            <div className="border border-gray-300">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left font-semibold">Payment Type</th>
                    <th className="px-4 py-3 text-left font-semibold">Due Date</th>
                    <th className="px-4 py-3 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-300">
                    <td className="px-4 py-3">Initial Deposit</td>
                    <td className="px-4 py-3 font-mono">{quote.depositDueDate ? formatDate(quote.depositDueDate) : 'Upon Booking'}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">{formatCurrency(quote.depositAmount)}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Final Payment</td>
                    <td className="px-4 py-3 font-mono">TBD</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">
                      {formatCurrency(quote.totalPrice - quote.depositAmount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notes Section */}
        {quote.notes && (
          <div className="mt-8">
            <h2 className="text-lg font-bold text-black mb-4 uppercase tracking-wide">Additional Information</h2>
            <div className="border border-gray-300 p-6 bg-gray-50">
              <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                {quote.notes}
              </p>
            </div>
          </div>
        )}

        {/* Action Section */}
        {onPayment && (
          <div className="mt-8 border-t-2 border-black pt-8">
            <button
              onClick={onPayment}
              disabled={isAgentPreview}
              className={`w-full bg-black text-white font-semibold py-4 px-6 uppercase tracking-wider text-sm transition-all ${
                isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
              }`}
            >
              {isAgentPreview ? 'Preview Mode - Payment Disabled' : 'Proceed to Payment'}
            </button>
            {!isAgentPreview && (
              <p className="text-center text-xs text-gray-500 mt-4 uppercase tracking-wide">
                Secure Payment Processing
              </p>
            )}
          </div>
        )}

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 border-t border-gray-300 pt-8">
            <div className="bg-gray-50 border border-gray-300 rounded p-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Briefcase className="w-5 h-5 text-gray-700" />
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Executive Travel Coordinator</h3>
              </div>
              
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-lg font-bold text-gray-900">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-gray-600 text-sm font-semibold">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-mono"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_email && quote.agent_phone && (
                  <span className="hidden sm:block text-gray-400">|</span>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-mono"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-300 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wider">
            Executive Travel Services • Confidential Document
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveBriefTemplate;
